import { useCallback, useEffect, useRef, useState } from 'react';

import { useRereadWhenVisible } from '../reread-when-visible';
import {
  loadWorkspaceHomeWithFallback,
  publishWorkspaceHomeReadShadowEvidence,
} from './workspace-home-read-seam';

/**
 * The per-source envelope the deployed `loadWorkHomeProjection` callable wraps every home
 * source in. `status: 'unavailable'` (and a `null` data) is the server's own honest "this
 * source could not be read", never a zero.
 */
export type WorkHomeSourceState<T> = Readonly<{
  status: 'complete' | 'partial' | 'unavailable';
  checkedAt: string;
  data: T | null;
  cursor: string | null;
}>;

export type WorkHomeSafetyBoard = Readonly<{
  publishedCount: number;
  items: readonly Readonly<{
    resourceId: string;
    category: string;
    publishedAt: string;
    routeId: string | null;
  }>[];
}>;

export type WorkHomeDueAction = Readonly<{
  kind: 'approval' | 'finding';
  resourceId: string;
  dueAt: string;
  routeId: string;
}>;

/**
 * Response shape of the deployed `loadWorkHomeProjection` callable
 * (work-home-projection-production-wave, region `asia-northeast3`). The whole projection is
 * modelled here -- not only the notification count the home badge needs today -- because the
 * port already returns all of it and approvals / findings / dueActions are the next consumers.
 */
export type WorkHomeProjection = Readonly<{
  selectedScopeId: string;
  favoriteRouteIds: readonly string[];
  startRouteId: string;
  compact: boolean;
  safetyBoard: WorkHomeSourceState<WorkHomeSafetyBoard>;
  notifications: WorkHomeSourceState<Readonly<{ unreadCount: number }>>;
  approvals: WorkHomeSourceState<Readonly<{ pendingCount: number }>>;
  findings: WorkHomeSourceState<Readonly<{ unresolvedCount: number }>>;
  dueActions: readonly WorkHomeDueAction[];
}>;

const record = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);
const text = (value: unknown): value is string => typeof value === 'string';
const nullableText = (value: unknown): value is string | null => value === null || text(value);
const textList = (value: unknown): value is string[] => Array.isArray(value) && value.every(text);
const tally = (value: unknown): value is number =>
  typeof value === 'number' && Number.isInteger(value) && value >= 0;

const sourceState = <T>(
  value: unknown,
  data: (candidate: unknown) => candidate is T,
): value is WorkHomeSourceState<T> =>
  record(value) &&
  (value.status === 'complete' || value.status === 'partial' || value.status === 'unavailable') &&
  text(value.checkedAt) &&
  nullableText(value.cursor) &&
  (value.data === null || data(value.data));

const safetyBoard = (value: unknown): value is WorkHomeSafetyBoard =>
  record(value) &&
  tally(value.publishedCount) &&
  Array.isArray(value.items) &&
  value.items.every(
    (item) =>
      record(item) &&
      text(item.resourceId) &&
      text(item.category) &&
      text(item.publishedAt) &&
      nullableText(item.routeId),
  );

const unreadCount = (value: unknown): value is Readonly<{ unreadCount: number }> =>
  record(value) && tally(value.unreadCount);
const pendingCount = (value: unknown): value is Readonly<{ pendingCount: number }> =>
  record(value) && tally(value.pendingCount);
const unresolvedCount = (value: unknown): value is Readonly<{ unresolvedCount: number }> =>
  record(value) && tally(value.unresolvedCount);

const dueAction = (value: unknown): value is WorkHomeDueAction =>
  record(value) &&
  (value.kind === 'approval' || value.kind === 'finding') &&
  text(value.resourceId) &&
  text(value.dueAt) &&
  text(value.routeId);

/**
 * Validates a `loadWorkHomeProjection` response. A malformed response invalidates the whole
 * projection (`null`) rather than being partially salvaged into numbers the server never sent.
 */
export function decodeWorkHomeProjection(value: unknown): WorkHomeProjection | null {
  if (!record(value)) return null;
  if (!text(value.selectedScopeId) || typeof value.startRouteId !== 'string') return null;
  if (!textList(value.favoriteRouteIds) || typeof value.compact !== 'boolean') return null;
  if (!sourceState(value.safetyBoard, safetyBoard)) return null;
  if (!sourceState(value.notifications, unreadCount)) return null;
  if (!sourceState(value.approvals, pendingCount)) return null;
  if (!sourceState(value.findings, unresolvedCount)) return null;
  if (!Array.isArray(value.dueActions) || !value.dueActions.every(dueAction)) return null;
  return {
    selectedScopeId: value.selectedScopeId,
    favoriteRouteIds: value.favoriteRouteIds,
    startRouteId: value.startRouteId,
    compact: value.compact,
    safetyBoard: value.safetyBoard,
    notifications: value.notifications,
    approvals: value.approvals,
    findings: value.findings,
    dueActions: value.dueActions,
  };
}

/** `null` means "could not load" -- it is never a stand-in for an all-zero projection. */
export interface WorkHomeRepository {
  load(selectedScopeId: string | null, signal?: AbortSignal): Promise<WorkHomeProjection | null>;
}

/**
 * Fallback used when the production key is unset (local/dev) and until the production
 * repository has been constructed. It reports "unknown", never a fabricated count.
 */
export class UnconfiguredWorkHomeRepository implements WorkHomeRepository {
  async load(): Promise<WorkHomeProjection | null> {
    return null;
  }
}

/** Transport shape returned by `@kop/operations-firebase-adapter`'s `createWorkHomeProjectionPort`. */
type WorkHomeProjectionCallablePort = {
  loadWorkHome(selectedScopeId?: string | null): Promise<unknown>;
};

/** Read-only transport shape returned by `createWorkspaceHomeReadPort`. */
type WorkspaceHomeReadCallablePort = {
  load(selectedScopeId?: string | null): Promise<unknown>;
};

/**
 * Wires the home screen to the deployed `loadWorkHomeProjection` callable via the shared,
 * already-authenticated Firebase app. Any failure -- network, App Check, a rejected callable,
 * or a malformed response -- resolves to `null` ("could not load"), never to a number.
 */
class ProductionWorkHomeRepository implements WorkHomeRepository {
  constructor(
    private readonly port: WorkHomeProjectionCallablePort,
    private readonly workspaceHomeReadPort: WorkspaceHomeReadCallablePort,
    private readonly environment: Readonly<{ VITE_WORKSPACE_HOME_READ_MODE?: string }>,
  ) {}

  async load(
    selectedScopeId: string | null,
    signal?: AbortSignal,
  ): Promise<WorkHomeProjection | null> {
    void signal; // the deployed callable transport has no cancellation hook to wire this to.
    try {
      return await loadWorkspaceHomeWithFallback({
        environment: this.environment,
        selectedScopeId,
        legacy: {
          load: async (scopeId) => decodeWorkHomeProjection(await this.port.loadWorkHome(scopeId)),
        },
        bff: this.workspaceHomeReadPort,
        decodeProjection: decodeWorkHomeProjection,
        onShadowEvidence: publishWorkspaceHomeReadShadowEvidence,
      });
    } catch {
      return null;
    }
  }
}

/**
 * Selects the production Firebase transport when `VITE_CONTENT_DATA_ADAPTER=firebase` -- the
 * same key `createBriefingRepository` uses for the other server-backed home surface. The key
 * stays unset in `.env.production.local`, so turning this on is a separate decision; with it
 * unset the home badge reports "unknown" rather than inventing a count.
 */
async function buildWorkHomeRepository(): Promise<WorkHomeRepository> {
  if (import.meta.env.VITE_CONTENT_DATA_ADAPTER === 'firebase') {
    const config = {
      projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID ?? '',
      apiKey: import.meta.env.VITE_FIREBASE_API_KEY ?? '',
      appId: import.meta.env.VITE_FIREBASE_APP_ID ?? '',
      authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN ?? '',
      appCheckSiteKey: import.meta.env.VITE_FIREBASE_APPCHECK_SITE_KEY ?? '',
    };
    if (Object.values(config).some((value) => value.trim().length === 0))
      throw new Error('업무 홈 Firebase 환경 설정이 완전하지 않습니다.');
    // Constructed lazily here, on first use (component mount, after sign-in) -- never at
    // module scope -- and only via the shared authenticated app that
    // createWorkHomeProjectionPort reuses internally, never a second Firebase app instance.
    const { createWorkHomeProjectionPort, createWorkspaceHomeReadPort } =
      await import('@kop/operations-firebase-adapter');
    return new ProductionWorkHomeRepository(
      createWorkHomeProjectionPort(config) as WorkHomeProjectionCallablePort,
      createWorkspaceHomeReadPort(config) as WorkspaceHomeReadCallablePort,
      { VITE_WORKSPACE_HOME_READ_MODE: import.meta.env.VITE_WORKSPACE_HOME_READ_MODE },
    );
  }
  return new UnconfiguredWorkHomeRepository();
}

let repositoryInFlight: Promise<WorkHomeRepository> | null = null;

/**
 * Built once per document, on first use. The port holds no identity of its own -- the callable
 * reads the signed-in account at call time -- so one instance serves the whole session, exactly
 * as `createSafetyBoardRepository` does for the board.
 */
export function createWorkHomeRepository(): Promise<WorkHomeRepository> {
  repositoryInFlight ??= buildWorkHomeRepository().catch((error) => {
    repositoryInFlight = null;
    throw error;
  });
  return repositoryInFlight;
}

/**
 * The home's own read, started before the home exists.
 *
 * This is not a cache and holds nothing from an earlier session: it is one request, made in
 * this document, whose answer the home is still waiting for when it mounts. The selected scope is
 * recorded with the request so a response can only be handed to the matching workspace.
 */
let prefetchedProjection: Promise<WorkHomeProjection | null> | null = null;
let prefetchedProjectionScopeId: string | null = null;

export function prefetchWorkHomeProjection(selectedScopeId: string | null): void {
  if (prefetchedProjection && prefetchedProjectionScopeId === selectedScopeId) return;
  prefetchedProjectionScopeId = selectedScopeId;
  prefetchedProjection = createWorkHomeRepository()
    .then((repository) => repository.load(selectedScopeId))
    .catch(() => null);
}

/**
 * Hands the warm-started read to its first matching caller and forgets it. A response for one
 * selected site is never offered to another selected site or to an unscoped HQ workspace.
 */
export function takeWorkHomeProjectionPrefetch(
  selectedScopeId: string | null,
): Promise<WorkHomeProjection | null> | null {
  if (selectedScopeId !== prefetchedProjectionScopeId || !prefetchedProjection) return null;
  const started = prefetchedProjection;
  prefetchedProjection = null;
  prefetchedProjectionScopeId = null;
  return started;
}

export function discardWorkHomeProjectionPrefetch(): void {
  prefetchedProjection = null;
  prefetchedProjectionScopeId = null;
}

export type WorkHomeSnapshot =
  | Readonly<{ state: 'loading' }>
  | Readonly<{ state: 'unavailable' }>
  | Readonly<{ state: 'loaded'; projection: WorkHomeProjection }>;

export const WORK_HOME_VISIBLE_STALE_MS = 60_000;

export function workHomeVisibleRefreshIsDue(
  lastCompletedAt: number | null,
  inFlight: boolean,
  now = Date.now(),
): boolean {
  return (
    !inFlight && (lastCompletedAt === null || now - lastCompletedAt >= WORK_HOME_VISIBLE_STALE_MS)
  );
}

export function useWorkHomeProjection(selectedScopeId: string | null = null, enabled = true): WorkHomeSnapshot {
  const [snapshot, setSnapshot] = useState<WorkHomeSnapshot>({ state: 'loading' });
  const [reading, setReading] = useState(0);
  const readInFlightRef = useRef(false);
  const lastReadCompletedAtRef = useRef<number | null>(null);
  const reread = useCallback(() => {
    if (!enabled) return;
    if (!workHomeVisibleRefreshIsDue(lastReadCompletedAtRef.current, readInFlightRef.current))
      return;
    // Claim the refresh before React schedules the effect so repeated visibility events cannot
    // enqueue duplicate reads in the same render window.
    readInFlightRef.current = true;
    setReading((value) => value + 1);
  }, [enabled]);
  useRereadWhenVisible(reread);
  useEffect(() => {
    if (!enabled) return;
    const abort = new AbortController();
    readInFlightRef.current = true;
    /* Only the first read of the document may accept the warm start. A later read is issued only
     * after the visible-return gate has found the completed projection stale. */
    const inFlight =
      (reading === 0 ? takeWorkHomeProjectionPrefetch(selectedScopeId) : null) ??
      createWorkHomeRepository().then((repository) =>
        repository.load(selectedScopeId, abort.signal),
      );
    void inFlight
      .then((projection) => {
        if (abort.signal.aborted) return;
        setSnapshot(projection ? { state: 'loaded', projection } : { state: 'unavailable' });
      })
      .catch(() => {
        if (!abort.signal.aborted) setSnapshot({ state: 'unavailable' });
      })
      .finally(() => {
        if (abort.signal.aborted) return;
        lastReadCompletedAtRef.current = Date.now();
        readInFlightRef.current = false;
      });
    return () => {
      abort.abort();
      readInFlightRef.current = false;
    };
  }, [reading, selectedScopeId, enabled]);
  return enabled ? snapshot : { state: 'unavailable' };
}

/** How far a source's count got: not resolved yet, resolved, or not obtainable. */
export type WorkHomeCountStatus = 'pending' | 'ready' | 'unavailable';

/** Kept for the notification bell, which named this status before the other sources existed. */
export type UnreadNoticeStatus = WorkHomeCountStatus;

export type WorkHomeCount = Readonly<{ status: WorkHomeCountStatus; count: number | null }>;

/**
 * Shared shape behind every per-source projection below: `loading`/`unavailable` at the whole-
 * snapshot level carry no count, and a source whose own envelope resolved to `data: null` is
 * `unavailable` too -- only an envelope that actually decoded data is `ready`, `0` included.
 */
function sourceCountOf<T>(
  snapshot: WorkHomeSnapshot,
  source: (projection: WorkHomeProjection) => WorkHomeSourceState<T>,
  countOf: (data: T) => number,
): WorkHomeCount {
  if (snapshot.state === 'loading') return { status: 'pending', count: null };
  if (snapshot.state === 'unavailable') return { status: 'unavailable', count: null };
  const selected = source(snapshot.projection);
  return selected.status === 'complete' && selected.data
    ? { status: 'ready', count: countOf(selected.data) }
    : { status: 'unavailable', count: null };
}

/**
 * Projects the server's notification source into the badge's three honest states. A `ready`
 * count of 0 is a real "nothing unread" the server reported; `pending` and `unavailable` carry
 * no count at all, so nothing downstream can render a number for them.
 */
export function unreadNoticeCountOf(snapshot: WorkHomeSnapshot): WorkHomeCount {
  return sourceCountOf(
    snapshot,
    (projection) => projection.notifications,
    (data) => data.unreadCount,
  );
}

/**
 * Same discipline as `unreadNoticeCountOf`, for the approvals source that feeds the 전자결재 menu
 * badge -- `electronic-approval` owns every approval request, so its pending count is exactly
 * `approvals.pendingCount`.
 */
export function pendingApprovalCountOf(snapshot: WorkHomeSnapshot): WorkHomeCount {
  return sourceCountOf(
    snapshot,
    (projection) => projection.approvals,
    (data) => data.pendingCount,
  );
}

/**
 * Same discipline, for the findings source that feeds the 점검 menu badge. Unresolved findings
 * (판정 미흡 + 조치 대기) are read exclusively through `InspectionReadWorkspace`'s "미조치" view,
 * which the `inspection-reporting` feature is the only menu entry that opens -- the individual
 * finding screens (`inspection-unresolved`, `facility-findings`, `psm-findings`, `finding-action`)
 * are reached from inside it and carry `navigation: false`, so they never appear in the menu on
 * their own.
 */
export function unresolvedFindingCountOf(snapshot: WorkHomeSnapshot): WorkHomeCount {
  return sourceCountOf(
    snapshot,
    (projection) => projection.findings,
    (data) => data.unresolvedCount,
  );
}

/**
 * `dueActions` has no per-source envelope of its own -- it is a flat list on the whole
 * projection -- so its honesty states come from the snapshot itself: `loading`/`unavailable`
 * carry no count, and once the snapshot is `loaded` the count is exactly how many due items of
 * that kind the server actually sent (`0` included). Every item is `kind: 'approval'` or
 * `kind: 'finding'`, the same two sources as above, so a due count is reported "on theirs": the
 * same 전자결재 / 점검 badges, never a third menu location.
 */
export function dueActionCountOf(
  snapshot: WorkHomeSnapshot,
  kind: WorkHomeDueAction['kind'],
): WorkHomeCount {
  if (snapshot.state === 'loading') return { status: 'pending', count: null };
  if (snapshot.state === 'unavailable') return { status: 'unavailable', count: null };
  const source = kind === 'approval' ? snapshot.projection.approvals : snapshot.projection.findings;
  if (source.status !== 'complete') return { status: 'unavailable', count: null };
  return {
    status: 'ready',
    count: snapshot.projection.dueActions.filter((action) => action.kind === kind).length,
  };
}
